import camelCase from 'lodash.camelcase';
import * as React from 'react';
import { memo } from 'react';

import Award from 'components/atoms/Award';
import { AwardProps } from 'components/atoms/Award';

import { EventTypes, getEventImageSrc } from 'defs/events';
import { EventDto } from 'defs/events';

import { format } from 'helper/dateTime';
import i18n from 'helper/i18n';
import config from 'helper/mapBackendConfig';
import { template } from 'helper/string';

export type ModifiersType = 'progress' | 'filled' | 'text' | 'overlay' | '';

export const FAME_BADGE_MODIFIERS = {
	PROGRESS: 'progress',
	FILLED: 'filled',
	TEXT: 'text',
	OVERLAY: 'overlay',
	EMPTY: '',
};

export const FAME_BADGE_DEFAULT_TITLES = {
	EMPTY: '?',
};

export type FameBadgeProps = {
	topic?: string;
	label?: string;
	award: AwardProps;
	modifier?: ModifiersType;
	doAddEvents?: (evs: any[]) => void; // TODO: Check is required
	showNotification?: boolean; // Opens the Badge in a notification
	fallback: {
		text?: string;
		title?: string;
	};
};

class FameBadge extends React.Component<FameBadgeProps> {
	public static displayName = 'FameBadge';
	public static defaultProps = {
		modifier: FAME_BADGE_MODIFIERS.EMPTY,
		showNotification: true,
		fallback: {
			title: FAME_BADGE_DEFAULT_TITLES.EMPTY,
		},
	};

	get infoText(): string {
		const { award } = this.props;
		if (award && award.awardedDateTime && award.awardedDateTime.date) {
			const formatedDate: string = format(award.awardedDateTime.date);
			return template(i18n(`react.impulsCoach.components.fameBadge.reachedOnDate`), { date: formatedDate }, true);
		}
		return i18n(`react.impulsCoach.components.fameBadge.notReachedYet`);
	}

	get imgSrc(): string {
		const { award } = this.props;
		if (award && !award.awardedDateTime) {
			return `${config.env.baseUrlUi}/assets/img/svg/award/sticker.svg`;
		}

		return '';
	}

	get text(): string {
		const { award } = this.props;
		if (award && !award.awardedDateTime) {
			return i18n(
				`react.impulsCoach.events.${camelCase(EventTypes.Badge)}.${camelCase(award.code)}.${camelCase(
					award.eventLevel
				)}.notReachedYet.text`
			);
		}

		return '';
	}

	public handleClick = (ev: React.MouseEvent<any>): any => {
		const { showNotification, award, doAddEvents, fallback, modifier } = this.props;

		if (showNotification === false || typeof award !== 'object') {
			return void 0;
		}

		const badgeModifier = this.isReached()
			? modifier
			: fallback.title === FAME_BADGE_DEFAULT_TITLES.EMPTY
			? FAME_BADGE_MODIFIERS.FILLED
			: FAME_BADGE_MODIFIERS.TEXT;

		if (doAddEvents && award.code && award.eventLevel) {
			let attributes: EventDto = {
				eventLevel: award.eventLevel,
				code: award.code,
				eventType: EventTypes.Badge,
				awardedDateTime: award.awardedDateTime,
				infoText: this.infoText,
				overwrites: {
					text: this.text,
				},
				modifier: 'badge',
			} as any;

			if (this.isReached()) {
				attributes = {
					...attributes,
					overwrites: {
						...attributes.overwrites,
						image: getEventImageSrc(award.code, EventTypes.Badge as any, award.eventLevel),
					},
				};
			} else {
				const notificationFameBadgeProps = {
					...this.props,
					modifier: badgeModifier, // Set the modifier of the badge
					showNotification: false, // Disable the click behavior
					label: '', // Remove the label
				};

				attributes.fameBadge = <FameBadge {...(notificationFameBadgeProps as FameBadgeProps)} />;
			}

			doAddEvents([attributes]);
		}
	};

	public isReached = (): boolean => {
		const { award } = this.props;

		return !!(award && award.awardedDateTime);
	};

	public render() {
		const { topic, modifier, award, label, fallback = {} } = this.props;
		const awarded = award && award.awardedDateTime;

		return (
			<div
				className={`m-fame-badge js-ui-overlay ${
					modifier && modifier !== FAME_BADGE_MODIFIERS.EMPTY ? `m-fame-badge--${modifier}` : ''
				}`}
				// href={label || 'unknown-badge'}
				onClick={this.handleClick}
			>
				{this.isReached() && (
					<div className="m-fame-badge__award">
						<Award modifier="success" {...award} />
					</div>
				)}
				<div className={`m-fame-badge__circle${topic && awarded ? `  m-fame-badge__circle--${topic}` : ''}`}>
					{!this.isReached() && (
						<div className="m-fame-badge__circle-info">
							<span className="m-fame-badge__circle-title">
								{fallback.title || FAME_BADGE_DEFAULT_TITLES.EMPTY}
							</span>
							{fallback.text && <span className="m-fame-badge__circle-text">{fallback.text}</span>}
						</div>
					)}
				</div>
				{!!label && (
					<div
						className="m-fame-badge__label"
						dangerouslySetInnerHTML={{ __html: label } as any}
						onClick={this.handleClick as any}
					/>
				)}
			</div>
		);
	}
}

export { FameBadge };

export default memo(FameBadge as any);
